import React from 'react';
import { Target, Clock, Star, Play } from 'lucide-react';

const Drills = () => {
  const drills = [
    {
      id: 1,
      title: 'Stick Handling Basics',
      description: 'Improve your puck control with fundamental stick handling drills',
      duration: '15 min',
      difficulty: 'Beginner',
      category: 'Stick Handling',
      image: '🏒'
    },
    {
      id: 2,
      title: 'Skating Edge Work',
      description: 'Master your edges for better agility and speed',
      duration: '20 min',
      difficulty: 'Intermediate',
      category: 'Skating',
      image: '⛸️'
    },
    {
      id: 3,
      title: 'Shot Accuracy',
      description: 'Practice your shooting accuracy and power',
      duration: '25 min',
      difficulty: 'Advanced',
      category: 'Shooting',
      image: '🎯'
    },
    {
      id: 4,
      title: 'Passing Drills',
      description: 'Improve your passing accuracy and timing',
      duration: '18 min',
      difficulty: 'Intermediate',
      category: 'Passing',
      image: '🏐'
    }
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-gray-900">Drills</h1>
        <p className="text-gray-600">Practice your skills with targeted drills</p>
      </div>

      {/* Categories */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {['All', 'Stick Handling', 'Skating', 'Shooting', 'Passing'].map((category) => (
          <button
            key={category}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            {category}
          </button>
        ))}
      </div>

      {/* Drills Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {drills.map((drill) => (
          <div key={drill.id} className="card hover:shadow-md transition-shadow">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <span className="text-3xl">{drill.image}</span>
                <div className="flex items-center space-x-2">
                  <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                    drill.difficulty === 'Beginner' ? 'bg-green-100 text-green-800' :
                    drill.difficulty === 'Intermediate' ? 'bg-yellow-100 text-yellow-800' :
                    'bg-red-100 text-red-800'
                  }`}>
                    {drill.difficulty}
                  </span>
                </div>
              </div>
              
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                {drill.title}
              </h3>
              
              <p className="text-gray-600 text-sm mb-4">
                {drill.description}
              </p>
              
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4 text-sm text-gray-500">
                  <div className="flex items-center">
                    <Clock size={16} className="mr-1" />
                    {drill.duration}
                  </div>
                  <div className="flex items-center">
                    <Target size={16} className="mr-1" />
                    {drill.category}
                  </div>
                </div>
                
                <button className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors">
                  <Play size={16} className="mr-2" />
                  Start
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Recommended Section */}
      <div className="card p-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">Recommended for You</h2>
        <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-lg p-6">
          <div className="flex items-center">
            <div className="w-16 h-16 bg-gradient-to-r from-blue-500 to-purple-600 rounded-lg flex items-center justify-center">
              <Star className="text-white" size={24} />
            </div>
            <div className="ml-4">
              <h3 className="text-lg font-semibold text-gray-900">Personalized Training Plan</h3>
              <p className="text-gray-600">Based on your recent games, we recommend focusing on skating and stick handling.</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Drills; 